import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
    Box, Button, Container, Stack, TextField, Typography,
    Paper, InputAdornment, InputLabel, Select, FormControl
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BreadCrumbs from "../components/Breadcrumbs";
import { PLUGINS_ROUTE } from "../utils/consts";
import {getPluginDetails, updatePluginSetting} from "../http/PluginAPI";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';

const PluginDetails = () => {
    const { plugin_id } = useParams();
    const navigate = useNavigate();
    const [plugin, setPlugin] = useState(null);
    const [pluginName, setPluginName] = useState('');
    const [pluginVersion, setPluginVersion] = useState('');
    const [provider, setProvider] = useState(1);
    const [editable, setEditable] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [commands, setCommands] = useState('');
    const [extraFiles, setExtraFiles] = useState([{ path: '', file: null }]);
    const [file, setFile] = useState(null);

    const hrefs = [{ href: PLUGINS_ROUTE, name: "Список плагинов" }];

    useEffect(() => {
        getPluginDetails(plugin_id).then(data => {
          setPlugin(data);
          setName(data.name || '');
          setPluginName(data.plugin_name || '');
          setPluginVersion(data.version || '');
          setProvider(data.provider_id || '');
          setDescription(data.description || '');
          setCommands(data.commands || '');
          setEditable(data.editable || false);

          const additions = (data.additions || []).map(add => ({
                path: add.path.replace(/^\/?oxide\//, ''), // убираем /oxide/ префикс
                file: null,
                existing: true, // 👈 специальный флаг
                filename: add.filename,
                size: add.size,
                addition_id: add.addition_id ?? 0,
                editable: add.editable ?? false,
          }));
          setExtraFiles([...additions]);
        });
    }, [plugin_id]);

    const handleAddExtraFile = () => {
        setExtraFiles([...extraFiles, { path: '', file: null }]);
    };

    const handleExtraFileChange = (index, field, value) => {
        const updated = [...extraFiles];
        updated[index][field] = value;
        setExtraFiles(updated);
    };

    const handleSave = async () => {
        try {
            // Сохраняем текстовые поля
            const formData = new FormData();
            formData.append('plugin_id', plugin_id);
            formData.append('name', name);
            formData.append('plugin_name', pluginName);
            formData.append('provider', provider);
            formData.append('version', pluginVersion);
            formData.append('description', description);
            formData.append('commands', commands);

            if (file) formData.append('file', file);

            // Загружаем дополнительные файлы, если есть
            extraFiles.forEach((entry, index) => {
                if (entry.file && entry.path) {
                    formData.append(`files[${index}][file]`, entry.file);
                    formData.append(`files[${index}][path]`, `/oxide/${entry.path}`);
                }
            });

            const response = await updatePluginSetting(formData);
            console.log('Ответ от сервера:', response);
            console.log('Изменения сохранены');
        } catch (e) {
            console.error(e);
            console.log('Ошибка при сохранении');
        }
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%', marginTop: 3, marginBottom: 3 }}>
                <BreadCrumbs hrefs={hrefs} name={pluginName} />
            </Box>
            <Typography sx={{ mb: 3 }} variant="h5" gutterBottom>Плагин: {pluginName}</Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Имя</Typography>
                <TextField
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Имя плагина</Typography>
                <TextField
                    fullWidth
                    value={pluginName}
                    onChange={(e) => setPluginName(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Версия</Typography>
                <TextField
                    fullWidth
                    value={pluginVersion}
                    onChange={(e) => setPluginVersion(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Источник</Typography>
                <FormControl fullWidth>
                    <Select
                        labelId="edit-provider"
                        value={provider}
                        label="Источник"
                        onChange={e => setProvider(e.target.value)}
                    >
                        <MenuItem value={1}>Umod</MenuItem>
                        <MenuItem value={2}>Oxide-russia.ru</MenuItem>
                        <MenuItem value={3}>Codefling</MenuItem>
                        <MenuItem value={4}>Moscow.ovh</MenuItem>
                        <MenuItem value={5}>Topplugin.ru</MenuItem>
                    </Select>
                </FormControl>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Описание</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Команды</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={commands}
                    onChange={(e) => setCommands(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Основной файл</Typography>
                {plugin?.filename && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {plugin.filename && (
                            <Typography variant="body2" color="text.secondary">
                                {plugin.filename} — {plugin.size} КБ
                            </Typography>
                        )}
                        {plugin.editable && (
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                    navigate(`/plugin/${plugin_id}/edit/0/${encodeURIComponent(plugin.filename)}`)
                                }
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                )}
                <Button variant="outlined" component="label">
                    Загрузить файл
                    <input
                        hidden
                        type="file"
                        onChange={e => setFile(e.target.files[0])}
                    />
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1">Дополнительные файлы</Typography>
                {extraFiles.map((entry, index) => (
                    <Stack key={index} direction="column" spacing={1} sx={{ mt: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Путь относительно /oxide/"
                                fullWidth
                                value={entry.path}
                                onChange={(e) => handleExtraFileChange(index, 'path', e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">/oxide/</InputAdornment>,
                                    readOnly: entry.existing
                                }}
                            />
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadFileIcon />}
                            >
                                Загрузить файл
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => handleExtraFileChange(index, 'file', e.target.files[0])}
                                />
                            </Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {entry.filename && (
                                <Typography variant="body2" color="text.secondary">
                                    {entry.filename} — {entry.size} КБ
                                </Typography>
                            )}
                            {entry.editable && (
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                        navigate(`/plugin/${plugin_id}/edit/${entry.addition_id || 0}/${encodeURIComponent(entry.filename)}`)
                                    }
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                ))}
                <Button sx={{ mt: 2 }} onClick={handleAddExtraFile}>
                    + Добавить ещё файл
                </Button>
            </Paper>

            <Box textAlign="right">
                <Button variant="contained" onClick={handleSave}>
                    Сохранить изменения
                </Button>
            </Box>
        </Container>
    );
};

export default PluginDetails;
